import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link, Navigate } from 'react-router-dom';

import { CenterFrameLayout } from 'Layouts';
import { PauseModal } from 'Features/PauseSubscription/PauseModal';
import { PillButton } from 'Components/Fields/Buttons';
import { Dropdown } from 'Components/Dropdown';
import { getNumberOfDaysForPause } from 'Utils/Date';
import { usePauseSubscription } from 'Hooks/usePauseSubscription';
import { trackEvent, TRACKING_ENUMS } from 'Utils/Analytics';
import { usePlans } from 'Hooks/usePlans';

import './PauseSubscriptionContainer.css';

const PAUSE_DURATIONS = [
  {
    text: '1 Month',
    value: 1,
  },
  {
    text: '2 Months',
    value: 2,
  },
  {
    text: '3 Months',
    value: 3,
  },
];

export const PauseSubscriptionContainer = () => {
  const [selectedDuration, setSelectedDuration] = useState({ text: '', value: '' });
  const [showModal, toggleModal] = useState(false);
  const [isPauseSuccess, setIsPauseSuccess] = useState(false);

  const routes = useSelector((s) => s.appConfig.routes);
  const DURATIONS = useMemo(() => PAUSE_DURATIONS, []);
  const { enablePauseSubscription } = useSelector((s) => s.appConfig.defaultConfig);
  const { nextBillInfo } = useSelector((s) => s.payments);
  const { activeSubscriptionId, activeSubscriptionServiceName, isFetching } = useSelector((s) => s.subscription);

  const plans = usePlans(activeSubscriptionId);
  const { selectedPlanPrice } = plans;
  const nextBillingDateTime = nextBillInfo && nextBillInfo.nextBillingDateTime;
  const [pauseState, pauseActions] = usePauseSubscription(true);

  const durationOptions = useMemo(() => {
    if (pauseState.isReady) {
      return DURATIONS.map((d) => ({ ...d, value: getNumberOfDaysForPause(nextBillingDateTime, d.value) })).filter(
        (d) => d.value <= pauseState.noOfDaysLeft
      );
    }
    return [];
  }, [DURATIONS, nextBillingDateTime, pauseState]);

  useEffect(() => {
    if (pauseState.isReady && durationOptions.length > 0) {
      const pauseStartedData = {
        page_plan_id: activeSubscriptionId,
        page_plan_name: activeSubscriptionServiceName,
        page_plan_price: parseFloat(selectedPlanPrice),
        page_plan_pause_length: durationOptions[0].text,
        page_start_source: 'settings',
      };
      trackEvent(TRACKING_ENUMS.EVENT_ACCOUNT_PAUSE_STARTED, pauseStartedData, pauseStartedData);
    }
  }, [pauseState, activeSubscriptionId, activeSubscriptionServiceName, selectedPlanPrice, durationOptions]);

  const handlePauseSubscription = async () => {
    const payload = {
      numOfDays: selectedDuration.value,
      serviceID: activeSubscriptionId,
    };

    const { isSuccess, error } = await pauseActions.pause(payload);

    if (isSuccess) {
      const confirmedCompletedData = {
        page_plan_id: activeSubscriptionId,
        page_plan_name: activeSubscriptionServiceName,
        page_plan_price: parseFloat(selectedPlanPrice),
        page_plan_pause_length: selectedDuration.text,
        page_start_source: 'settings',
        page_item_title: 'OK',
      };
      trackEvent(
        TRACKING_ENUMS.EVENT_ACCOUNT_PAUSE_CONFIRMED_COMPLETED,
        confirmedCompletedData,
        confirmedCompletedData
      );
    } else {
      const pauseError = {
        page_error_description: error,
        page_error_type: 'server-side',
      };
      trackEvent(TRACKING_ENUMS.EVENT_ACCOUNT_PAUSE_ERROR, pauseError, pauseError);
    }

    setIsPauseSuccess(isSuccess);
  };

  const pauseActionEvent = (itemTitle) => {
    const pauseData = {
      page_plan_id: activeSubscriptionId,
      page_plan_name: activeSubscriptionServiceName,
      page_plan_price: parseFloat(selectedPlanPrice),
      page_plan_pause_length: selectedDuration.text,
      page_start_source: 'settings',
      page_item_title: itemTitle,
    };
    trackEvent(TRACKING_ENUMS.EVENT_ACCOUNT_PAUSE_ACTION, pauseData, pauseData);
  };

  if (!enablePauseSubscription || isPauseSuccess || !nextBillingDateTime) {
    return <Navigate replace to={routes.myAccount} />;
  }

  return (
    <CenterFrameLayout
      title="Pause Your Subscription"
      showLoader={durationOptions.length === 0}
      customClass="pauseContainer"
    >
      <div className="pauseWrapper">
        <div className="header">Select how long to pause your subscription</div>
        <Dropdown options={durationOptions} onSelect={setSelectedDuration} />
        <PillButton
          text="Pause Subscription"
          onClick={() => {
            pauseActionEvent('Pause Subscription');
            toggleModal(true);
          }}
        />
        <Link to={routes.myAccount} onClick={() => pauseActionEvent('Cancel')} className="cancelLink">
          Cancel
        </Link>
      </div>
      <PauseModal
        handlePauseSubscription={handlePauseSubscription}
        isFetching={isFetching}
        isShowing={showModal}
        nextBillingDateTime={nextBillingDateTime}
        onClose={() => toggleModal(false)}
        activeSubscriptionId={activeSubscriptionId}
        activeSubscriptionServiceName={activeSubscriptionServiceName}
        selectedPlanPrice={selectedPlanPrice}
        selectedDuration={selectedDuration}
      />
    </CenterFrameLayout>
  );
};
